import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useUnit } from "effector-react";
import { FundingGoal, FundingGoalEntityType } from "../model/types";
import { goalsService } from "../service";
import {
  fetchGoalsRequested,
  getEntityCallRequested,
  $goalsStore,
} from "../store";
import { CreateGoalModal } from "./CreateGoalModal";
import { useToast } from "@/core/hooks/useToast";

export interface GoalsListProps {
  goals: FundingGoal[];
  onOpenGoal: (goalId: string) => void;
  onFindMatches?: (goalId: string) => void;
}

export const GoalsList: React.FC<GoalsListProps> = ({
  goals,
  onOpenGoal,
  onFindMatches,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<FundingGoal | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const { entity } = useUnit($goalsStore);
  const fetchGoals = useUnit(fetchGoalsRequested);
  const getEntityCall = useUnit(getEntityCallRequested);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleMenuToggle = (e: React.MouseEvent, goalId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === goalId ? null : goalId);
  };

  const handleEdit = (e: React.MouseEvent, goal: FundingGoal) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setSelectedGoal(goal);
    setIsEditModalOpen(true);
    getEntityCall({ goalId: goal.id });
  };

  const handleDelete = async (e: React.MouseEvent, goalId: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    try {
      await goalsService.deleteGoal(goalId);
      fetchGoals(undefined);
      toast.showSuccess("Goal deleted successfully");
    } catch (error) {
      console.error("Failed to delete goal", error);
      toast.showError("Failed to delete goal");
    }
  };

  const handleModalHide = () => {
    setIsEditModalOpen(false);
    setSelectedGoal(null);
    // fetchGoals(undefined);
  };

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647"; // green
    if (66 >= score && score >= 34) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  return (
    <>
      <div className="goals-grid">
        {goals.map((goal) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onFindMatches?.(goal.id);
            }}
            key={goal.id}
            className="goal-card"
            role="button"
          >
            <div className="goal-card__title-row">
              <h3>{goal.title}</h3>
              {goal.max_score && (
                <Tag
                  value={`up to ${goal.max_score || 0}% match`}
                  className="match-badge"
                  style={{
                    backgroundColor: getScoreColor(Number(goal.max_score)),
                  }}
                />
              )}
              <div
                className="summary-actions"
                ref={openMenuId === goal.id ? menuRef : null}
              >
                <button
                  className="kebab-btn"
                  onClick={(e) => handleMenuToggle(e, goal.id)}
                  aria-label="Open menu"
                >
                  <i className="pi pi-ellipsis-v" />
                </button>
                {openMenuId === goal.id && (
                  <div className="summary-dropdown">
                    <button
                      className="dropdown-item"
                      onClick={(e) => handleEdit(e, goal)}
                    >
                      <i className="pi pi-cog" /> Edit goal
                    </button>
                    <button
                      className="dropdown-item danger"
                      onClick={(e) => handleDelete(e, goal.id)}
                    >
                      <i className="pi pi-trash" /> Delete goal
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="goal-card-items">
              <div className="goal-card__left">
                <div className="goal-card__meta-row">
                  {goal.expenditure && (
                    <span className="meta-label">Category</span>
                  )}
                  {goal.expenditure && (
                    <Tag value={goal?.expenditure?.name} className="chip" />
                  )}

                  {goal?.range?.name && (
                    <span className="meta-label">Budget</span>
                  )}
                  {goal.range?.name && (
                    <Tag value={goal.range?.name} className="chip" />
                  )}

                  {goal.types && goal.types.length > 0 && (
                    <span className="meta-label">Type</span>
                  )}
                  {goal.types?.map((t) => (
                    <Tag key={t.id} value={t.name} className="chip" />
                  ))}
                </div>

                {goal.sectors && goal.sectors.length > 0 && (
                  <div className="goal-card__sectors-row">
                    {goal.sectors.slice(0, 4).map((s) => (
                      <Tag key={s.id} value={s.name} className="chip" />
                    ))}
                    {goal.sectors.length > 4 && (
                      <Tag
                        value={`+${goal.sectors.length - 4}`}
                        className="chip"
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="goal-card__cta">
                <Button
                  label={`Find ${goal.matching_calls} matching calls`}
                  icon="pi pi-arrow-right"
                  iconPos="right"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedGoal && (
        <CreateGoalModal
          visible={isEditModalOpen}
          onHide={handleModalHide}
          goal={selectedGoal}
          entity={entity as FundingGoalEntityType}
        />
      )}
    </>
  );
};
